import T from 'Components/Helpers/translate';
import type { AutonomyBoundaries, CrmAction, PermissionLevel } from 'Resources/Agents';

// Pure mapping from an agent's `autonomyBoundaries` JSON to the grants the
// permissions UI renders (WEB-7960 — split out of WorkspaceAgentPage).

export const CAPABILITY_PREFIX = 'cap:';

/**
 * A grant key's display name, falling back to the raw key when it has no
 * translation (WEB-11381). Grants exist on keys no screen names — alias keys the
 * earn loop wrote, entities added to the gate since — and showing `account`
 * beats showing `assistant.entity_account`, which is what `T` returns on a miss.
 */
export const entityLabel = (entity: string) => {
	const key = `assistant.entity_${entity}`;
	const label = T(key);
	return label === key ? entity : label;
};

/**
 * "modify" is the user-facing label for the `update` action. Takes a plain
 * string because the inbox's upgrade requests carry an unvalidated one.
 */
export const actionLabelKey = (action: string) => `assistant.perm_${action === 'update' ? 'modify' : action}`;

/** As `entityLabel`, for a capability grant. */
export const capabilityLabel = (capability: string) => {
	const key = `assistant.cap_${capability}`;
	const label = T(key);
	return label === key ? capability : label;
};

/** A capability's one-line explainer, or '' when it has no translation. */
export const capabilitySubLabel = (capability: string) => {
	const key = `assistant.cap_${capability}_sub`;
	const label = T(key);
	return label === key ? '' : label;
};

export const PERMISSION_LEVELS: PermissionLevel[] = ['read', 'modify', 'create', 'delete'];

export const CRM_ACTIONS: CrmAction[] = ['update', 'create', 'delete'];

// Maps the legacy cumulative ladder grant onto the actions it covers.
const LADDER_TO_ACTIONS: Record<PermissionLevel, CrmAction[]> = {
	read: [],
	modify: ['update'],
	create: ['update', 'create'],
	delete: ['update', 'create', 'delete']
};

/**
 * The actions a legacy ladder value covers, or [] if it isn't a ladder value.
 * Callers that revoke a single action need this to know what the ladder key was
 * granting, so the remaining actions can be pinned back explicitly.
 */
export const ladderActions = (value: unknown): CrmAction[] =>
	typeof value === 'string' && PERMISSION_LEVELS.includes(value as PermissionLevel)
		? LADDER_TO_ACTIONS[value as PermissionLevel]
		: [];

export type EntityActionGrants = Record<string, Partial<Record<CrmAction, true>>>;
/**
 * Keyed by whatever capability the agent holds, not by a fixed union — a grant
 * on one the UI doesn't know about still has to render (WEB-11381).
 */
export type CapabilityGrants = Partial<Record<string, boolean>>;

export const boundariesToActionGrants = (map: AutonomyBoundaries | null | undefined): EntityActionGrants => {
	if (!map) return {};
	const result: EntityActionGrants = {};
	for (const [key, val] of Object.entries(map)) {
		if (key.startsWith(CAPABILITY_PREFIX)) continue;

		// Per-action grant: "entity:action" → true
		if (key.includes(':')) {
			const [entity, action] = key.split(':', 2);
			if (!CRM_ACTIONS.includes(action as CrmAction)) continue;
			if (val !== true) continue;
			(result[entity] = result[entity] || {})[action as CrmAction] = true;
			continue;
		}

		// Legacy ladder grant: "entity" → PermissionLevel
		if (key.includes('.')) continue;
		if (typeof val !== 'string' || !PERMISSION_LEVELS.includes(val as PermissionLevel)) continue;
		const actions = LADDER_TO_ACTIONS[val as PermissionLevel];
		if (actions.length === 0) continue;
		const bucket = (result[key] = result[key] || {});
		for (const a of actions) bucket[a] = true;
	}
	return result;
};

export const boundariesToCapabilities = (map: AutonomyBoundaries | null | undefined): CapabilityGrants => {
	if (!map) return {};
	const result: CapabilityGrants = {};
	for (const [key, val] of Object.entries(map)) {
		if (val === true && key.startsWith(CAPABILITY_PREFIX)) {
			result[key.slice(CAPABILITY_PREFIX.length)] = true;
		}
	}
	return result;
};

/**
 * The boundaries map after granting or revoking one autonomy key
 * (`entity:action`, or `cap:<capability>`). Pure so the ladder handling below is
 * testable on its own — the caller only persists the result.
 *
 * Revoking one action off a legacy ladder grant (`contact: 'create'`, which
 * covers update AND create) has to retire the ladder key and pin the kept
 * actions back explicitly, or the ladder silently re-grants what was revoked.
 */
export const nextBoundaries = (
	current: AutonomyBoundaries | null | undefined,
	key: string,
	granted: boolean
): AutonomyBoundaries => {
	const next: AutonomyBoundaries = { ...(current ?? {}) };
	if (granted) {
		next[key] = true;
		return next;
	}

	delete next[key];
	const [entity, action] = key.split(':', 2);
	const covered = entity && action ? ladderActions(next[entity]) : [];
	if (covered.length) {
		delete next[entity];
		for (const kept of covered) {
			if (kept !== action) next[`${entity}:${kept}`] = true;
		}
	}
	return next;
};
